import { v4 as uuidv4 } from 'uuid';

/**
 * Módulo de Serviço de Webhook via Server-Sent Events (SSE) e Polling Adaptativo
 * Permite conexão de alta performance em tempo real entre o backend (Webhook)
 * e o front-end chat-web no navegador.
 */

let activePollingBoostUntil = 0;
const pollingTriggers = new Set();

/**
 * Ativa temporariamente o modo de polling de alta frequência (ex: após envio de mensagens)
 * para que respostas da First Layer apareçam instantaneamente.
 * 
 * @param {number} durationMs Duração em milissegundos do modo acelerado (padrão: 30s)
 */
export function accelerateWebhookPolling(durationMs = 30000) {
    activePollingBoostUntil = Math.max(activePollingBoostUntil, Date.now() + durationMs);
    pollingTriggers.forEach(trigger => trigger());
}

export const latestInputControls = (messages = []) => {
    const latest = [...messages].reverse().find(message => message?.origin !== 'you' && message?.origin !== 'user');
    return {
        disabled: latest?.bloqueiaInput === true || latest?.typing === true || (latest?.origin === 'bot' && latest?.response === 'Digitando...'),
        numeric: latest?.bloqueiaInputLetras === true
    };
};

/**
 * Converte o payload do Webhook em uma ou mais mensagens formatadas do Chat.
 */
export function formatWebhookMessage(webhookPayload) {
    if (!webhookPayload) return [];

    if (typeof webhookPayload === 'string') {
        if (!webhookPayload.trim()) return [];
        try {
            webhookPayload = JSON.parse(webhookPayload);
        } catch (e) {
            return [{
                id: `wh-${uuidv4()}`,
                origin: 'bot',
                response: webhookPayload,
                text: webhookPayload,
                timestamp: new Date().toISOString(),
                isWebhook: true,
                source: 'webhook-sse'
            }];
        }
    }

    // Se já for uma lista de mensagens
    if (Array.isArray(webhookPayload)) {
        return webhookPayload.flatMap(item => formatWebhookMessage(item));
    }

    if (webhookPayload && typeof webhookPayload === 'object') {
        if (Array.isArray(webhookPayload.messages)) {
            return webhookPayload.messages.flatMap(item => formatWebhookMessage({ ...webhookPayload, messages: undefined, ...item }));
        }
        if (Array.isArray(webhookPayload.data)) {
            return webhookPayload.data.flatMap(item => formatWebhookMessage({ ...webhookPayload, data: undefined, ...item }));
        }
        if (webhookPayload.payload && typeof webhookPayload.payload === 'object') {
            webhookPayload = { ...webhookPayload.payload, ...webhookPayload };
        } else if (webhookPayload.data && typeof webhookPayload.data === 'object' && !Array.isArray(webhookPayload.data)) {
            webhookPayload = { ...webhookPayload.data, ...webhookPayload };
        }
    }

    const textContent = String(webhookPayload.message || webhookPayload.response || webhookPayload.text || '');
    const origin = webhookPayload.origin || webhookPayload.sender || 'bot';
    const options = webhookPayload.options || webhookPayload.buttons || [];
    const carousel = webhookPayload.carrossel || webhookPayload.carousel || null;
    const hasRenderableContent = (value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') return Object.keys(value).length > 0;
        return String(value || '').trim().length > 0;
    };
    const hasComponent = ['ocrApenasPedidoMedico', 'ocrCarteirinha', 'ocrCarteirinhaNegativo',
        'componenteConvenio', 'buscaExamesTextual', 'componenteRemocaoProcedimentos', 'iniciarPagamento',
        'buttonLink', 'typing', 'isConfirmandoAgendamento', 'isBuscaExamesLoading',
        'isBuscaExamesOcrLoading', 'isOcrCarteirinhaLoading'].some(key => webhookPayload[key] === true)
        || ['perguntasApoio', 'examesAutorizacao'].some(key => hasRenderableContent(webhookPayload[key]));
    if (!textContent.trim() && !hasRenderableContent(options) && !hasRenderableContent(carousel) && !hasComponent) return [];
    const richFields = (last) => ({
        flowSessionId: webhookPayload.flowSessionId,
        typing: last && !!webhookPayload.typing,
        isConfirmandoAgendamento: last && !!webhookPayload.isConfirmandoAgendamento,
        isBuscaExamesLoading: last && !!webhookPayload.isBuscaExamesLoading,
        isBuscaExamesOcrLoading: last && !!webhookPayload.isBuscaExamesOcrLoading,
        isOcrCarteirinhaLoading: last && !!webhookPayload.isOcrCarteirinhaLoading,
        buscaExamesTextual: last && !!webhookPayload.buscaExamesTextual,
        allowVaccines: last && !!webhookPayload.allowVaccines,
        buscaExamesMovel: last && !!webhookPayload.buscaExamesMovel,
        ocrApenasPedidoMedico: last && !!webhookPayload.ocrApenasPedidoMedico,
        ocrCarteirinha: last && !!webhookPayload.ocrCarteirinha,
        ocrCarteirinhaNegativo: last && !!webhookPayload.ocrCarteirinhaNegativo,
        componenteConvenio: last && !!webhookPayload.componenteConvenio,
        componenteRemocaoProcedimentos: last && !!webhookPayload.componenteRemocaoProcedimentos,
        examesSelecionados: last ? (webhookPayload.examesSelecionados || null) : null,
        iniciarPagamento: last && !!webhookPayload.iniciarPagamento,
        valorPagamento: last ? (webhookPayload.valorPagamento ?? null) : null,
        perguntasApoio: last ? (webhookPayload.perguntasApoio || null) : null,
        examesAutorizacao: last ? (webhookPayload.examesAutorizacao || null) : null,
        buttonLink: last && !!webhookPayload.buttonLink,
        buttonLinkText: last ? (webhookPayload.buttonLinkText || '') : '',
        requisicaoAgendamento: last ? (webhookPayload.requisicaoAgendamento || '') : '',
        providerKey: last ? (webhookPayload.providerKey || null) : null,
        idPessoaFisica: last ? (webhookPayload.idPessoaFisica || '') : '',
        preselectedInsuranceId: last ? (webhookPayload.preselectedInsuranceId || '') : '',
        forceManualConvenioFlow: last && !!webhookPayload.forceManualConvenioFlow,
        unidadeSelecionadaNome: last ? (webhookPayload.unidadeSelecionadaNome || '') : '',
        respostaExtensa: last && !!webhookPayload.respostaExtensa,
        detalhesOcultos: last ? (webhookPayload.detalhesOcultos || []) : [],
        detalhesOcultosTag: last ? (webhookPayload.detalhesOcultosTag || '') : '',
    });
    const baseId = webhookPayload.id || webhookPayload.message_id || webhookPayload.messageId || `wh-${uuidv4()}`;

    // Suporte ao padrão Fleury de separação por "$#$"
    if (textContent.includes('$#$')) {
        const segments = textContent.split('$#$');
        return segments.map((seg, idx) => {
            const isLast = idx === segments.length - 1;
            return {
                id: `${baseId}-${idx}`,
                origin: origin,
                response: seg.trim(),
                text: seg.trim(),
                timestamp: webhookPayload.timestamp || new Date().toISOString(),
                isWebhook: true,
                source: webhookPayload.source || 'webhook-sse',
                isFirstMessage: idx === 0,
                buttons: isLast ? options : [],
                options: isLast ? options : [],
                carrossel: isLast ? (webhookPayload.carrossel || webhookPayload.carousel || null) : null,
                bloqueiaInput: isLast ? !!webhookPayload.bloqueiaInput : false,
                bloqueiaInputLetras: isLast ? !!webhookPayload.bloqueiaInputLetras : false,
                desativaFeedbackPorMensagem: webhookPayload.desativaFeedbackPorMensagem || false,
                ...richFields(isLast),
                meta: webhookPayload.meta || webhookPayload.metadata || {}
            };
        });
    }

    return [{
        id: baseId,
        origin: origin,
        response: textContent,
        text: textContent,
        timestamp: webhookPayload.timestamp || new Date().toISOString(),
        isWebhook: true,
        source: webhookPayload.source || 'webhook-sse',
        buttons: options,
        options: options,
        carrossel: webhookPayload.carrossel || webhookPayload.carousel || null,
        bloqueiaInput: !!webhookPayload.bloqueiaInput,
        bloqueiaInputLetras: !!webhookPayload.bloqueiaInputLetras,
        desativaFeedbackPorMensagem: webhookPayload.desativaFeedbackPorMensagem || false,
        ...richFields(true),
        meta: webhookPayload.meta || webhookPayload.metadata || {}
    }];
}

/**
 * Poll conversation queues in acknowledged mode. The callback must return true
 * (or resolve true) after accepting a message, including an already-rendered duplicate.
 * Rejections, cleanup and stale conversation responses leave messages queued.
 */
export function startWebhookSSEListener({ flowSessionId, onMessageReceived, onError }) {
    const baseUrl = process.env.REACT_APP_WEBHOOK_URL || process.env.REACT_APP_API_BASE_URL;
    let listening = true;
    let timer = null;
    let controller = null;
    let inFlight = false;
    let expedited = false;
    let acknowledgedCapability = false;
    const conversation = () => typeof flowSessionId === 'function' ? flowSessionId() : flowSessionId;
    // The webhook Lambda does not require a bearer token on stream/ack: provider callbacks use their own auth-*-token headers.
    const headers = { Accept: 'application/json' };

    const emit = async (raw, scope) => {
        const messages = formatWebhookMessage(raw);
        if (!messages.length) return false;
        for (const message of messages) {
            if (!listening || conversation() !== scope) return false;
            if (typeof onMessageReceived !== 'function' || await onMessageReceived(message) !== true) return false;
        }
        return true;
    };
    const schedule = delay => {
        if (!listening) return;
        clearTimeout(timer);
        timer = setTimeout(poll, delay);
    };
    const poll = async () => {
        if (!listening || inFlight) return;
        const scope = conversation();
        if (!baseUrl || !scope) { schedule(1500); return; }
        inFlight = true;
        expedited = false;
        controller = new AbortController();
        try {
            // Probe safely before any GET: older servers silently drain their queue.
            if (!acknowledgedCapability) {
                const capability = await fetch(`${baseUrl}/webhook/ack`, { method: 'OPTIONS', headers, cache: 'no-store', signal: controller.signal });
                if (!capability.ok || (await capability.json()).deliveryProtocol !== 'ack-v1') throw new Error('Webhook server does not support acknowledged delivery');
                acknowledgedCapability = true;
            }
            if (!listening || conversation() !== scope) return;
            const query = new URLSearchParams({ delivery: 'ack', flowSessionId: scope, _t: String(Date.now()) });
            const response = await fetch(`${baseUrl}/webhook/stream?${query}`, { method: 'GET', headers, cache: 'no-store', signal: controller.signal });
            if (!response.ok) throw new Error(`Webhook read failed: ${response.status}`);
            const data = await response.json();
            if (data.deliveryProtocol !== 'ack-v1' || data.flowSessionId !== scope || !Array.isArray(data.messages)) {
                acknowledgedCapability = false;
                throw new Error('Invalid acknowledged webhook response');
            }
            for (const raw of data.messages || []) {
                if (!listening || conversation() !== scope) break;
                if (!raw.id || raw.flowSessionId !== scope) continue;
                if (!await emit(raw, scope) || !listening || conversation() !== scope) continue;
                const ack = await fetch(`${baseUrl}/webhook/ack`, {
                    method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, signal: controller.signal,
                    body: JSON.stringify({ flowSessionId: scope, messageIds: [raw.id] }),
                });
                if (!ack.ok) throw new Error(`Webhook acknowledgement failed: ${ack.status}`);
            }
        } catch (error) {
            if (listening && error.name !== 'AbortError' && typeof onError === 'function') onError(error);
        } finally {
            inFlight = false;
            controller = null;
            schedule(expedited ? 50 : Date.now() < activePollingBoostUntil ? 500 : 1500);
        }
    };
    const boost = () => {
        if (!listening) return;
        if (inFlight) expedited = true;
        else schedule(50);
    };
    pollingTriggers.add(boost);
    const custom = event => { if (event?.detail) emit(event.detail, conversation()).catch(error => onError?.(error)); };
    const windowMessage = event => {
        const data = event?.data;
        if (data && (data.__webhook_event || ['webhook_message', 'chat:webhook_message'].includes(data.type) || data.action === 'webhook_message')) {
            emit(data.payload || data.detail || data.data || data, conversation()).catch(error => onError?.(error));
        }
    };
    if (typeof window !== 'undefined') {
        window.addEventListener('chat:webhook_message', custom);
        window.addEventListener('message', windowMessage);
    }
    poll();
    return () => {
        listening = false;
        pollingTriggers.delete(boost);
        clearTimeout(timer);
        controller?.abort();
        if (typeof window !== 'undefined') {
            window.removeEventListener('chat:webhook_message', custom);
            window.removeEventListener('message', windowMessage);
        }
    };
}
